import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgClass, NgStyle } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import {
  ButtonActivity,
  CustomItem,
  DonationItem,
  ENVIRONMENT,
  Environment,
  InputActivity,
  ItemMeta,
  Lead,
  PageActivity,
  sizeDescriptions,
  Specification,
} from '@domains';
import { CustomItemService } from '@rspl-api';
import {
  Designable,
  DesignService,
  DisableDoubleClickDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
  ResponsiveService,
} from '@rspl-ui';
import { Observable, take } from 'rxjs';

export interface ItemPreviewDialogData {
  lead: Lead;
  item: DonationItem;
  request_id: string;
  restrictedItems?: string[];
  createLeadActivity?: (
    leadId: string | undefined | null,
    type: PageActivity | ButtonActivity | InputActivity,
    value?: any,
    request_id?: string,
  ) => Observable<any>;
  updateCart: (lead: Lead) => Observable<Lead>;
}

@Component({
  selector: 'app-item-preview-dialog',
  templateUrl: './item-preview-dialog.component.html',
  styleUrls: ['./item-preview-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    FxLayoutDirective,
    FxLayoutGapDirective,
    MatIcon,
    FxLayoutAlignDirective,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatRadioModule,
    MatFormFieldModule,
    NgStyle,
    DisableDoubleClickDirective,
  ],
})
export class ItemPreviewDialogComponent extends Designable implements OnInit {
  isCaptain: boolean;
  disassembly: boolean;
  count = 1;
  sizeDescriptions = sizeDescriptions;
  lead: Lead;
  size: 'xlarge' | 'large' | 'medium';
  public constructor(
    public reference: MatDialogRef<ItemPreviewDialogComponent>,
    @Inject(ENVIRONMENT) protected environment: Environment,
    @Inject(MAT_DIALOG_DATA) public data: ItemPreviewDialogData,
    protected override designService: DesignService,
    public override responsiveService: ResponsiveService,
    private customItemService: CustomItemService,
  ) {
    super(designService, responsiveService);
    this.isCaptain = this.environment.isCaptain;
    this.size = data.item.size as 'xlarge' | 'large' | 'medium';
  }

  override ngOnInit(): void {
    this.lead = new Lead(this.data.lead);
  }

  removeItem(): void {
    if (this.count > 1) {
      this.count = this.count - 1;
    }
  }

  addItem(): void {
    this.count = Math.min(this.count + 1, 500);
  }

  handleQuantityChangeThroughInput(event: any) {
    const value = Number(event.target.value.replace(/[^0-9]/g, ''));
    event.target.value = value;

    this.count = Math.min(value, 500);
  }

  submit(): void {
    if (this.count > 500) return;
    if (this.disassembly === true && (!this.isCaptain || this.size === 'xlarge')) {
      this.lead.specification.disassembly += this.count;
    }
    if (this.isCaptain && this.size !== this.data.item.size) {
      this.customItemService
        .create(
          new CustomItem({
            name: `${this.data.item.name} (${this.size === 'medium' ? 'Medium' : this.size === 'large' ? 'Large' : 'X-Large'})`,
            specification: new Specification({
              xlarge: this.size === 'xlarge' ? 1 : 0,
              large: this.size === 'large' ? 1 : 0,
              medium: this.size === 'medium' ? 1 : 0,
              disassembly: this.disassembly && this.size === 'xlarge' ? 1 : 0,
            }),
            meta: new ItemMeta({
              createdByCaptain: true,
              itemId: this.data.item.id,
            }),
          }),
        )
        .pipe(take(1))
        .subscribe((customItem) => {
          this.lead.aggregatedItems.push({
            item: new DonationItem({
              id: customItem.id,
              name: customItem.name,
              description: customItem.description,
              type: 'CustomItem',
              xlarge: customItem.specification.xlarge,
              large: customItem.specification.large,
              medium: customItem.specification.medium,
            }),
            count: this.count,
          });
          this.data.updateCart(this.lead).subscribe(() => {
            this.reference.close(true);
          });
        });
    } else {
      const existing = this.lead.aggregatedItems.find((item) => item.item.id === this.data.item.id && item.item.type === 'Item');
      if (existing) {
        existing.count = Math.min(existing.count + this.count, 500);
      } else {
        this.lead.aggregatedItems.push({ item: this.data.item, count: this.count });
      }
      this.data.updateCart(this.lead).subscribe({
        next: () => {
          if (this.data.createLeadActivity) {
            this.data
              .createLeadActivity(this.data.lead?.id, ButtonActivity.ITEM_ADDED, this.data.item.id, this.data.request_id)
              .pipe(take(1))
              .subscribe();
            if (this.disassembly === true) {
              this.data
                .createLeadActivity(this.data.lead?.id, ButtonActivity.DISASSEMBLY_ADDED, this.data.item.id, this.data.request_id)
                .pipe(take(1))
                .subscribe();
            }
          }
          this.reference.close(true);
        },
        error: (err) => {
          console.error('Error updating Cart:', err);
        },
      });
    }
  }
}
